import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import AccountPage from '../components/AccountPage';
import DocumentsHeader from '../components/DocumentsHeader';
import DocumentItemList from '../components/DocumentItemList';
import DeleteGroupDialogue from '../components/DeleteGroupDialogue';
import { FileAPI } from '../../../api';
import { FileStore } from '../../../store';
import { useSelector, useDispatch } from 'react-redux';
import notify from '../../../lib/notify';
import Pagination from '@material-ui/lab/Pagination';
import * as QueryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import Loader from '../../../components/PulseLoader';
import * as track from '../../../lib/track';

const LIMIT = 25;

const useStyles = makeStyles(() => ({
  pagination: {
    padding: '1rem 0',
  },
}));

const DocumentsPage = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const {isAuthenticated, profile} = useSelector(state => state.auth);
  const docs = useSelector(state => state.file.files);
  const loading = useSelector(state => state.file.isLoading);
  const total = useSelector(state => state.file.total);
  const urlParams = QueryString.parse(location.search);
  const page = ('page' in urlParams) ? parseInt(urlParams.page) : 1;
  const searchQuery = ('search' in urlParams) ? urlParams.search : '';
  const offset = (page * LIMIT) - LIMIT;
  const [pageCount, setPageCount] = useState(0);
  const showPagination = total > LIMIT;
  const [checked, setChecked] = useState([]);
  const [deleteGroupOpen, setDeleteGroupOpen] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const userUuid = profile.user_uuid;

  const getFiles = useCallback(async() => {
    if (!isAuthenticated) return;
    const query = searchQuery === '' ? null : searchQuery;
    const { payload, total } = await FileAPI.listFiles('participant', userUuid, query, LIMIT, offset);
    setPageCount(Math.ceil(total / LIMIT));
    dispatch(FileStore.setFiles(payload, total, LIMIT, offset));
  }, [searchQuery, offset, dispatch, isAuthenticated, userUuid]);

  const pushChange = (newPage, newSearchQuery) => {
    let params = {};
    if (newPage !== 1) {
      params['page'] = newPage;
    }
    if (newSearchQuery !== '') {
      params['search'] = newSearchQuery;
    }
    const queryParams = new URLSearchParams(params);
    history.push(`/profile/documents?${queryParams}`);
  };

  const onPageChange = (event, newPage) => {
    track.sendEvent('file page change', 'click', {page: newPage});
    pushChange(newPage, searchQuery);
  };

  const onSearchChange = newQuery => {
    track.sendEvent('file search query', 'submit', {});
    pushChange(page, newQuery);
  };

  const updateSubtitle = useCallback(() => {
    if (searchQuery === '') {
      setSubtitle(`You have ${total.toLocaleString()} documents online.`);
    } else {
      setSubtitle(`Found ${total.toLocaleString()} documents matching your search.`);
    }
  }, [total, searchQuery]);

  const deleteFile = uuid => {
    track.sendEvent('delete file', 'submit', {uuid: uuid});
    return FileAPI.deleteFile('participant', userUuid, uuid);
  };

  const renameFile = (uuid, value) => {
    track.sendEvent('rename file', 'submit', {uuid: uuid});
    return FileAPI.updateFile('participant', userUuid, uuid, {'fileName': value});
  };

  const setFileCategory = (uuid, value) => {
    track.sendEvent('set file category', 'submit', {uuid: uuid, value: value});
    return FileAPI.updateFile('participant', userUuid, uuid, {'category': value});
  };

  const checkFile = (event, doc) => {
    if (event.target.checked) {
      const addChecked = [...checked, {uuid: doc.uuid, name: doc.name}];
      setChecked(addChecked);
    } else {
      const removeChecked = checked.filter(d => d.uuid !== doc.uuid);
      setChecked(removeChecked);
    }
  };

  const downloadFile = async (uuid) => {
    track.sendEvent('download file', 'submit', {uuid: uuid});
    const { payload } = await FileAPI.getDownloadUrl('participant', userUuid, uuid);
    window.open(payload.signed_url, '_blank');
  };

  const openDeleteGroup = () => {
    track.sendEvent('delete checked items button', 'click', {});
    setDeleteGroupOpen(true);
  };

  useEffect(() => {
    updateSubtitle();
  }, [total, updateSubtitle]);

  useEffect(() => {
    getFiles();
    window.scrollTo(0, 0);
  }, [location, getFiles]);

  useEffect(() => {
    const handler = () => {
      getFiles();
    };
    notify.subscribe(handler, ['./file']);
    return () => notify.unsubscribe(handler);
  }, []); // eslint-disable-line

  return (
    <AccountPage showPlatformEmail={true}>
      <DocumentsHeader
        loading={loading}
        checkedCount={checked.length}
        showDeleteGroup={openDeleteGroup}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        subtitle={subtitle}
      />
      {loading ? (
        <Loader />
      ) : (
        <DocumentItemList
          docs={docs}
          checkFile={checkFile}
          downloadFile={downloadFile}
          renameFile={renameFile}
          deleteFile={deleteFile}
          setFileCategory={setFileCategory}
        />
      )}
      {showPagination ? (
        <div className={classes.pagination}>
          <Pagination
            count={pageCount}
            page={page}
            variant="outlined"
            shape="rounded"
            onChange={onPageChange}
          />
        </div>
      ) : null}
      <DeleteGroupDialogue
        open={deleteGroupOpen}
        onClose={()=>setDeleteGroupOpen(false)}
        checked={checked}
        setChecked={setChecked}
        deleteFile={deleteFile}
      />
    </AccountPage>
  );
};

export default DocumentsPage;
